import { Menubar } from "primereact/menubar";
import "primeicons/primeicons.css";
import React, { useEffect, useRef, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { MenuItem } from "primereact/menuitem";
import { useNavigate } from "react-router";
import FormDirtyListener from "../formListener/FormDirtyListener";
import { LeaveDialog } from "../form/components/leavePage/LeavePageWarning";
import { Role } from "../../shared/enums/Role";

export function Header() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [navigatePath, setNavigatePath] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(isDirty);
  const role = account !== undefined && account?.idTokenClaims !== undefined && account?.idTokenClaims["extension_Roles"];
  const Logout = async () => {
    try {
      await instance.logoutRedirect();
    } catch (error) {
      console.error(error);
    }
  };
  const [onHomePage, setOnHomePage] = useState(false);
  const activeMenuItemClass = "active-menu-item";

  function getMenu(): ExtendedMenuItem {
    switch (role) {
      case Role.assessor:
      case Role.manager:
      case Role.root: {
        return adminMenu(getCommand);
      }
      case Role.provider: {
        return providerMenu(getCommand);
      }
      case Role.kindergarten: {
        return kindergartenMenu(getCommand);
      }
      case Role.department: {
        return departmentMenu(getCommand);
      }
    }
    return {};
  }

  const [items, setItems] = useState<ExtendedMenuItem[]>([
    {
      id: "/home",
      label: "Home",
      command: () => {
        getCommandHome("/home");
      },
    },
    getMenu(),

    {
      label: "External Users",
      items: [
        {
          id: "/providers",
          label: "KIS Providers",
          command: () => {
            getCommand("/providers");
          },
        },
        {
          id: "/departmentUsers",
          label: "KIS DE",
          command: () => {
            getCommand("/departmentUsers");
          },
        },
      ],
      roles: [Role.manager, Role.assessor],
    },
    {
      id: "/referenceDataManagement",
      label: "Reference Data Management",
      command: () => {
        getCommand("/referenceDataManagement");
      },
      roles: [Role.manager],
    },
    {
      id: "/resources",
      label: "KIS Resources",
      command: () => {
        window.open("/resources", "_blank");
      },
    },
    {
      label: account?.name,
    },
    {
      label: "Logout",
      command: () => {
        Logout();
      },
    },
  ]);

  function getCommandHome(path: string) {
    setOnHomePage(true);
    if (isDirtyRef.current) {
      setShowDialog(true);
      setNavigatePath(path);
      return;
    } else {
      navigate(path);
    }
  }

  function getCommand(path: string) {
    setOnHomePage(false);
    if (isDirtyRef.current) {
      setShowDialog(true);
      setNavigatePath(path);
      return;
    } else {
      navigate(path);
    }
  }

  useEffect(() => {
    let newItems = [...items];
    const role = account != undefined && account?.idTokenClaims != undefined && account?.idTokenClaims["extension_Roles"];

    newItems = newItems.filter((i) => i.roles == null || i.roles.length === 0 || i.roles.includes(role as Role));

    if (onHomePage) {
      newItems = newItems.map((item) => ({ ...item, className: item.label === "Home" ? activeMenuItemClass : "" }));
    } else {
      newItems.forEach((i: any) => {
        if (isActive(i)) newItems = newItems.map((item) => ({ ...item, className: item.label === i.label ? activeMenuItemClass : "" }));
      });
    }

    setItems(newItems);
  }, [location.pathname, account, onHomePage]);

  const isActive = (item: MenuItem) => {
    if (location.pathname === item.id) {
      return true;
    } else if (item.items != null && items.length > 0) {
      let subSelected = false;
      item.items.forEach((i: any) => {
        const isSelected = isActive(i);
        if (isSelected) {
          subSelected = true;
        }
      });
      if (subSelected) {
        return true;
      }
    }
  };

  const handleLeavePage = () => {
    setIsDirty(false);
    isDirtyRef.current = false;
    setShowDialog(false);
    navigate(navigatePath);
    setOnHomePage(false);
  };

  const handleStay = () => {
    setShowDialog(false);
  };

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  return (
    <>
      {" "}
      <FormDirtyListener onDirtyChange={setIsDirty} />
      <div className="main-nav">
        <div className="flex justify-content-left kis-logo">
          <div className="flex text-blue font-bold justify-content-start text-left align-items-center flex-row">
            <div className="flex mt-1">
              <div className="header">
                <div className="text-purple text-3xl flex align-center ">
                  {" "}
                  <span className="kis-icon-purple"></span>Kindergarten Inclusion <br />
                  Support
                </div>
              </div>
            </div>
          </div>{" "}
        </div>
        <Menubar model={items} className="mr-3 ml-1 bg-none border-none user-name pl-0 flex justify-content-end drop-down dont-show-in-print" />
      </div>
      {showDialog && LeaveDialog(showDialog, handleStay, handleLeavePage)}
    </>
  );
}

interface ExtendedMenuItem extends MenuItem {
  roles?: Role[];
}

function adminMenu(getCommand: (path: string) => void): ExtendedMenuItem {
  return {
    id: "applications",
    label: "Applications",
    items: [
      {
        id: "/presubmission",
        label: "Presubmission",
        command: () => {
          getCommand("/presubmission");
        },
      },
      {
        id: "/myapplications",
        label: "My Applications",
        command: () => {
          getCommand("/myapplications");
        },
      },
      {
        id: "/applications",
        label: "Applications",
        command: () => {
          getCommand("/applications");
        },
      },
      {
        id: "/assessments",
        label: "Assessments",
        command: () => {
          getCommand("/assessments");
        },
      },
      {
        id: "/finalised",
        label: "Finalised",
        command: () => {
          getCommand("/finalised");
        },
      },
    ],
  };
}

function providerMenu(getCommand: (path: string) => void): ExtendedMenuItem {
  return {
    id: "/applications",
    label: "Applications",
    items: [
      {
        id: "/provider/applications",
        label: "Applications",
        command: () => {
          getCommand("/provider/applications");
        },
      },
    ],
  };
}

function kindergartenMenu(getCommand: (path: string) => void): ExtendedMenuItem {
  return {
    id: "applications",
    label: "Applications",
    items: [
      {
        id: "/kindergarten/submittedApplications",
        label: "Applications",
        command: () => {
          getCommand("/kindergarten/submittedApplications");
        },
      },
      {
        id: "/kindergarten/pendingApplications",
        label: "Applications Pending Submission",
        command: () => {
          getCommand("/kindergarten/pendingApplications");
        },
      },
    ],
  };
}

function departmentMenu(getCommand: (path: string) => void): ExtendedMenuItem {
  return {
    label: "Applications",
    items: [
      {
        id: "/department/applications",
        label: "Applications",
        command: () => {
          getCommand("/department/applications");
        },
      },
    ],
  };
}
